import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Gauge, AlertTriangle, Wrench, Boxes, PlayCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CodeBox, FeatureCard, DemoHero, Callout, useDocMeta } from '../components/esl/EslShared';

const EslDemoIndustrial = () => {
  useDocMeta(
    'BLOX ESL — Demo industrial (motor de reglas para planta)',
    'BLOX ESL como motor de reglas industrial: sensores en C, evaluar_planta() en BLOX. Umbrales, alarmas y paradas de emergencia configurables sin recompilar el host.'
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Header />

      <section className="border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <DemoHero
            eyebrow={<><Gauge className="w-3 h-3" /> Demo 2 de 3</>}
            title="Demo industrial"
            subtitle="BLOX ESL como motor de reglas para una planta. C genera lecturas de sensores (temperatura, presión, vibración, tanque) y BLOX decide actuadores, alarmas, mantenimiento y paradas de emergencia."
            accent="amber"
          />
        </div>
      </section>

      {/* CONTEXTO */}
      <section className="py-12 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Contexto</h2>
          <p className="text-slate-400 text-base sm:text-lg mb-6 leading-relaxed">
            En sistemas industriales las <span className="text-white">políticas y umbrales</span> cambian con más frecuencia que la infraestructura de adquisición. BLOX ESL permite mover esas reglas fuera del código C y editarlas sin recompilar el host.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FeatureCard icon={Gauge} title="Sensores en C">
              El host maneja adquisición, timestamps, buffers y comunicación con PLCs, drivers o sockets. BLOX no toca hardware.
            </FeatureCard>
            <FeatureCard icon={AlertTriangle} title="Reglas en BLOX">
              Umbrales, condiciones y decisiones viven en <code className="font-mono text-amber-300">industrial.blox</code>.
            </FeatureCard>
            <FeatureCard icon={Wrench} title="Actuadores en C">
              BLOX devuelve una tabla de acciones y el host aplica ventilador, bomba, válvula, mantenimiento y seguridad.
            </FeatureCard>
          </div>
        </div>
      </section>

      {/* UBICACIÓN Y EJECUCIÓN */}
      <section className="py-12 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Ubicación y ejecución</h2>
          <CodeBox filename="ruta" lang="text">{`integrations/demo-industrial-C`}</CodeBox>
          <CodeBox filename="run.ps1" lang="powershell">{`.\\integrations\\demo-industrial-C\\run.ps1`}</CodeBox>
          <p className="text-slate-400 text-sm">
            Compila con <code className="text-amber-300 font-mono">libblox_core.a</code> y ejecuta el host C que carga <code className="text-amber-300 font-mono">industrial.blox</code>.
          </p>
        </div>
      </section>

      {/* MAIN.C */}
      <section className="py-12 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">¿Qué hace <code className="font-mono text-amber-300">main.c</code>?</h2>
          <p className="text-slate-400 mb-6 leading-relaxed">
            Define la estructura <code className="font-mono text-amber-300">Sample</code> con las lecturas industriales, las convierte a JSON, las inyecta a BLOX como <code className="font-mono text-amber-300">TABLA</code> y aplica el resultado.
          </p>

          <CodeBox filename="main.c" lang="c">{`typedef struct Sample {
    const char *name;
    double temperature;
    double pressure;
    double vibration;
    double tank;
    int    emergency_stop;
} Sample;`}</CodeBox>

          <p className="text-slate-300 text-sm mb-2">El host registra su callback nativo:</p>
          <CodeBox filename="main.c" lang="c">{`blox_vm_register_native(
    vm,
    "HOST_EVENTO",
    2,
    BLOX_VALUE_EMPTY,
    host_evento,
    &planta
);`}</CodeBox>

          <p className="text-slate-300 text-sm mb-2">Cada muestra se serializa a JSON y se convierte a tabla:</p>
          <CodeBox filename="input.json" lang="json">{`[{
  "temperatura": 97.5,
  "presion": 9.1,
  "vibracion": 1.8,
  "tanque": 48.0,
  "paro_emergencia": false
}]`}</CodeBox>

          <CodeBox filename="main.c" lang="c">{`blox_vm_table_from_json(vm, json_view, &input);
blox_vm_call(vm, "evaluar_planta", &input, 1, &result);`}</CodeBox>

          <p className="text-slate-300 text-sm mt-4">
            El host lee la tabla devuelta y aplica cada fila como acción sobre sus actuadores:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-4">
            {['ventilador', 'bomba_tanque', 'valvula_alivio', 'mantenimiento', 'seguridad'].map(id => (
              <code key={id} className="px-3 py-2 rounded-lg bg-amber-500/5 border border-amber-500/20 text-amber-300 font-mono text-xs text-center">
                {id}
              </code>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIAL.BLOX */}
      <section className="py-12 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">¿Qué hace <code className="font-mono text-amber-300">industrial.blox</code>?</h2>
          <p className="text-slate-400 mb-6 leading-relaxed">
            Expone la función <code className="font-mono text-amber-300">evaluar_planta(lectura)</code>. Lee los sensores, aplica umbrales, dispara alarmas al host y devuelve la tabla de acciones.
          </p>

          <p className="text-slate-300 text-sm mb-2">Lee sensores desde la tabla de entrada:</p>
          <CodeBox filename="industrial.blox" lang="blox">{`FUNCION evaluar_planta(lectura)
INICIO
    NUMERICO temp, presion, vibracion, tanque

    temp      = lectura[1].temperatura
    presion   = lectura[1].presion
    vibracion = lectura[1].vibracion
    tanque    = lectura[1].tanque`}</CodeBox>

          <p className="text-slate-300 text-sm mb-2">Aplica reglas configurables:</p>
          <CodeBox filename="industrial.blox" lang="blox">{`SI (temp > 95) ENTONCES
    ventilador = 100
    estado     = "critico"
    mensaje    = "temperatura critica"
    HOST_EVENTO("alarma", "temperatura critica")
FIN_SI

SI (presion > 8.5) ENTONCES
    valvula_alivio = VERDADERO
    HOST_EVENTO("alarma", "presion elevada")
FIN_SI`}</CodeBox>

          <p className="text-slate-300 text-sm mb-2">Devuelve la tabla de acciones para el host:</p>
          <CodeBox filename="industrial.blox" lang="blox">{`AGREGAR(acciones, {
    id      = "seguridad",
    tipo    = "estado",
    valor   = 0,
    activo  = parada,
    estado  = estado,
    mensaje = mensaje
})

RETORNAR acciones
FINAL_FUNCION`}</CodeBox>

          <Callout tone="amber" label="Resultado esperado">
            <code className="font-mono text-amber-300">Resumen industrial: eventos=7 paradas=2</code>
            <p className="mt-2 text-slate-300">
              El operador puede ajustar los umbrales (95 °C, 8.5 bar…) editando <code className="font-mono text-amber-300">industrial.blox</code> sin tocar el binario C.
            </p>
          </Callout>
        </div>
      </section>

      {/* CTA cross-demo */}
      <section className="pt-16 pb-32 sm:pb-24 lg:pb-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Boxes className="w-12 h-12 text-amber-400 mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Explora las otras integraciones
          </h2>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/embedded-scripting-language/demo-godot"
              data-testid="industrial-to-godot"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 font-semibold hover:bg-slate-700 transition-colors"
            >
              <PlayCircle className="w-4 h-4" />
              Demo Godot
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/embedded-scripting-language/demo-negocios"
              data-testid="industrial-to-negocios"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 font-semibold hover:bg-slate-700 transition-colors"
            >
              Demo de negocios
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EslDemoIndustrial;
